import { useState } from "react";
import { useLicense } from "../../contexts/LicenseContext";
import { useAuth } from "../../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import {
  Crown,
  X,
  MessageCircle,
  Banknote,
  CheckCircle,
  Loader2,
  Send,
} from "lucide-react";
import axios from "axios";
import toast from "react-hot-toast";

const API = window.location.origin;

export default function UpgradeModal() {
  const { showUpgradeModal, closeUpgradeModal } = useLicense();
  const { token } = useAuth();
  
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!showUpgradeModal) return null;

  // إرسال طلب الشراء
  const submitPurchaseRequest = async () => {
    setIsSubmitting(true);
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.post(`${API}/api/referrals/submit-purchase`, {
        referral_code: "DIRECT",
        payment_method: "not_specified",
        notes: ""
      }, { headers });
      
      if (res.data.success) {
        toast.success("تم إرسال طلب الشراء! سيتم التواصل معك قريباً");
        closeUpgradeModal();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "حدث خطأ في إرسال الطلب");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    closeUpgradeModal();
  };

  // فتح واتساب
  const openWhatsApp = () => {
    const message = encodeURIComponent("مرحباً، أريد شراء تطبيق حسابـاتـي");
    window.open(`https://wa.me/9647772719901?text=${message}`, "_blank");
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <Card className="w-full max-w-md bg-gradient-to-br from-slate-900 to-slate-800 border-emerald-500/30 shadow-2xl shadow-emerald-500/10 max-h-[90vh] overflow-y-auto">
        <CardHeader className="text-center pb-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleClose}
            className="absolute left-4 top-4 text-slate-400 hover:text-white"
          >
            <X className="w-5 h-5" />
          </Button>
          
          <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center shadow-lg shadow-emerald-500/30">
            <Crown className="w-10 h-10 text-white" />
          </div>
          <CardTitle className="text-2xl text-white">
            تفعيل التطبيق الكامل
          </CardTitle>
          <p className="text-slate-400 mt-2">
            احصل على جميع الميزات بسعر موحد
          </p>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* السعر الموحد */}
          <div className="bg-gradient-to-br from-emerald-500/10 to-teal-500/10 border border-emerald-500/30 rounded-xl p-6 text-center">
            <p className="text-slate-400 text-sm mb-2">سعر التطبيق الكامل</p>
            <p className="text-4xl font-bold text-emerald-400">690,000</p>
            <p className="text-slate-500 mt-1">دينار عراقي - دفعة واحدة</p>
            
            <div className="mt-4 space-y-2 text-sm">
              <div className="flex items-center justify-center gap-2 text-slate-300">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                <span>جميع الميزات مفتوحة</span>
              </div>
              <div className="flex items-center justify-center gap-2 text-slate-300">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                <span>ترخيص دائم</span>
              </div>
              <div className="flex items-center justify-center gap-2 text-slate-300">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                <span>دعم فني</span>
              </div>
            </div>
          </div>

          {/* طرق الدفع */}
          <div className="bg-slate-800/50 rounded-xl p-4">
            <div className="flex items-center gap-2 text-slate-300 mb-3">
              <Banknote className="w-5 h-5 text-emerald-400" />
              <span className="font-semibold">طرق الدفع المتاحة</span>
            </div>
            <div className="grid grid-cols-2 gap-2 text-center text-sm">
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-2">
                <p className="text-amber-400 font-semibold">سوبر كي</p>
                <p className="text-slate-400 text-xs" dir="ltr">7115634573</p>
              </div>
              <div className="bg-slate-700/50 rounded-lg p-2">
                <p className="text-white">زين كاش</p>
                <p className="text-slate-400 text-xs" dir="ltr">07772719901</p>
              </div>
              <div className="bg-slate-700/50 rounded-lg p-2">
                <p className="text-white">آسيا باي</p>
                <p className="text-slate-400 text-xs" dir="ltr">07772719901</p>
              </div>
              <div className="bg-slate-700/50 rounded-lg p-2">
                <p className="text-white">ناس واليت</p>
                <p className="text-slate-400 text-xs" dir="ltr">07772719901</p>
              </div>
            </div>
          </div>

          {/* أزرار الإجراء */}
          <div className="space-y-3">
            {/* زر إرسال الطلب */}
            <Button
              onClick={submitPurchaseRequest}
              disabled={isSubmitting}
              className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white py-6 text-lg"
              data-testid="submit-purchase-btn"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 ml-2 animate-spin" />
                  جاري الإرسال...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5 ml-2" />
                  إرسال طلب الشراء
                </>
              )}
            </Button>
            
            {/* زر واتساب */}
            <Button
              onClick={openWhatsApp}
              className="w-full bg-green-600 hover:bg-green-700 text-white py-5"
              data-testid="whatsapp-btn"
            >
              <MessageCircle className="w-5 h-5 ml-2" />
              تواصل عبر واتساب
            </Button>
          </div>

          {/* معلومات التواصل */}
          <div className="text-center pt-4 border-t border-slate-700">
            <p className="text-slate-500 text-sm">للاستفسار والدعم الفني</p>
            <div className="flex justify-center gap-4 mt-2">
              <a href="tel:07772719901" className="text-emerald-400 hover:text-emerald-300" dir="ltr">
                07772719901
              </a>
              <span className="text-slate-600">|</span>
              <a href="tel:07507940933" className="text-emerald-400 hover:text-emerald-300" dir="ltr">
                07507940933
              </a>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
